import { useEffect, useState, useMemo } from "react";
import Pricing from "./Pricing";
import {
    Calendar,
    Clock,
    CreditCard,
    RefreshCw,
    AlertTriangle,
    CheckCircle,
    FileSpreadsheet,
    FileText,
    ChevronLeft,
    ChevronRight,
    Image,
} from "lucide-react";
import { API_BASE_URL } from "../components/config";
import { authFetch, SubscriptionError } from "../components/authfetch";
import "../css/Subscriptionpage.css";

export default function SubscriptionPage({
    currency = { symbol: "", currency: "" },
}) {
    const [data, setData] = useState(null);
    const [payments, setPayments] = useState([]);
    const [pricing, setPricing] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 5;
    const [activeTab, setActiveTab] = useState("success");

    useEffect(() => {
        const token = localStorage.getItem("token");
        if (!token) return;

        async function loadData() {
            try {
                // ── FIX: fetch all 3 in parallel ──────────────────────────────
                // Previously payment-history was never called, so `payments`
                // stayed [] forever and all tabs showed "No records".
                const [subscriptionRes, pricingRes, paymentsRes] =
                    await Promise.all([
                        authFetch(`${API_BASE_URL}/api/doctor/subscription`),
                        authFetch(`${API_BASE_URL}/api/admin/pricing`),
                        authFetch(
                            `${API_BASE_URL}/api/payment/payment-history`,
                        ),
                    ]);

                const subscriptionData = await subscriptionRes.json();
                const pricingData = await pricingRes.json();
                const paymentsData = await paymentsRes.json();

                if (subscriptionData.success) setData(subscriptionData);
                if (pricingData?.success) setPricing(pricingData?.pricing);

                // ── FIX: populate payments state ──────────────────────────────
                if (
                    paymentsData?.success &&
                    Array.isArray(paymentsData.payments)
                ) {
                    setPayments(paymentsData.payments);
                }
            } catch (err) {
                if (err instanceof SubscriptionError) {
                    setLoadError("subscription_expired");
                } else {
                    console.error("Subscription page error:", err);
                    setLoadError("fetch_failed");
                }
            }
        }

        loadData();
    }, []);

    useEffect(() => {
        setCurrentPage(1);
    }, [activeTab]);

    const sortedPayments = useMemo(
        () =>
            [...payments].sort(
                (a, b) => new Date(b.paidAt) - new Date(a.paidAt),
            ),
        [payments],
    );

    const successPayments = sortedPayments.filter(
        (p) => (p.status || "").toLowerCase() === "success",
    );

    const pendingPayments = sortedPayments.filter(
        (p) => (p.status || "").toLowerCase() === "pending",
    );

    const failedPayments = sortedPayments.filter(
        (p) => (p.status || "").toLowerCase() === "failed",
    );

    // ── Subscription expired: show minimal page with upgrade prompt ──
    if (loadError === "subscription_expired") {
        return (
            <div className="sp-root">
                <div className="sp-header">
                    <h1 className="sp-title">
                        Billing &amp; <em>Subscription</em>
                    </h1>
                </div>
                <div className="sp-card" style={{ "--accent": "#fb923c" }}>
                    <div className="sp-card-header">
                        <div
                            className="sp-card-icon"
                            style={{
                                background: "rgba(251,146,60,0.15)",
                                color: "#fb923c",
                            }}
                        >
                            <AlertTriangle size={15} />
                        </div>
                        <div className="sp-card-title">
                            Subscription Expired
                        </div>
                    </div>
                    <div className="sp-card-body">
                        <div className="sp-expired-alert">
                            <AlertTriangle
                                size={16}
                                style={{ flexShrink: 0 }}
                            />
                            <span>
                                Your subscription has expired.{" "}
                                <a href="#pricing" className="sp-upgrade-link">
                                    Renew below
                                </a>{" "}
                                to restore access to all features.
                            </span>
                        </div>
                    </div>
                </div>
                <div id="pricing">
                    <Pricing />
                </div>
            </div>
        );
    }

    if (loadError === "fetch_failed") {
        return (
            <div className="sp-root">
                <div className="sp-header">
                    <h1 className="sp-title">
                        Billing &amp; <em>Subscription</em>
                    </h1>
                </div>
                <div className="sp-card">
                    <div className="sp-card-body">
                        <div className="sp-expired-alert">
                            <AlertTriangle
                                size={16}
                                style={{ flexShrink: 0 }}
                            />
                            <span>
                                Failed to load subscription data. Please refresh
                                the page or contact support if the issue
                                persists.
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!data || !pricing) {
        return (
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "60vh",
                    gap: 8,
                }}
            >
                {[0, 0.2, 0.4].map((delay, i) => (
                    <span
                        key={i}
                        style={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            background: "#2e3d5c",
                            animation: `sp-pulse 1.2s ease-in-out ${delay}s infinite`,
                            display: "inline-block",
                        }}
                    />
                ))}
            </div>
        );
    }

    const { subscription, usage } = data;

    const normalizedStatus = (() => {
        if (!subscription.expiryDate) return subscription.status || "active";
        const today = new Date();
        const exp = new Date(subscription.expiryDate);
        today.setHours(0, 0, 0, 0);
        exp.setHours(0, 0, 0, 0);
        if (exp <= today) return "expired";
        return "active";
    })();

    const isExpired = normalizedStatus === "expired";

    const safeUsage = isExpired
        ? { excelExports: 0, invoiceDownloads: 0, imageUploads: 0 }
        : usage;
    const planKey = subscription.plan.toLowerCase();
    const planLimits =
        planKey === "free"
            ? { excelLimit: 0, invoiceLimit: 0, imageLimit: 0 }
            : (pricing?.[planKey] ?? {
                  excelLimit: 0,
                  invoiceLimit: 0,
                  imageLimit: 0,
              });

    const effectiveCurrency = {
        currency: subscription?.currency || currency?.currency || "INR",
        symbol: subscription?.currencySymbol || currency?.symbol || "₹",
    };
    const formatCurrency = (value) =>
        new Intl.NumberFormat(undefined, {
            style: "currency",
            currency: effectiveCurrency.currency,
            minimumFractionDigits: 0,
        }).format(value || 0);

    const baseExcelLimit = planLimits.excelLimit ?? -1;
    const baseInvoiceLimit = planLimits.invoiceLimit ?? -1;
    const baseImageLimit = planLimits.imageLimit ?? -1;

    const billingCycle = subscription.billingCycle || "monthly";

    const imageLimit =
        baseImageLimit === -1
            ? -1
            : billingCycle === "yearly"
              ? baseImageLimit * 12
              : baseImageLimit;

    const excelLimit =
        baseExcelLimit === -1
            ? -1
            : billingCycle === "yearly"
              ? baseExcelLimit * 12
              : baseExcelLimit;

    const invoiceLimit =
        baseInvoiceLimit === -1
            ? -1
            : billingCycle === "yearly"
              ? baseInvoiceLimit * 12
              : baseInvoiceLimit;

    const startDate = subscription.startDate
        ? new Date(subscription.startDate).toLocaleDateString("en-IN")
        : "-";

    const expiryDate = subscription.expiryDate
        ? new Date(subscription.expiryDate).toLocaleDateString("en-IN")
        : "No Expiry";

    const excelPercent =
        isExpired || excelLimit <= 0
            ? 0
            : excelLimit === -1
              ? safeUsage.excelExports > 0
                  ? 10
                  : 0
              : Math.min(100, (safeUsage.excelExports / excelLimit) * 100);

    const invoicePercent =
        isExpired || invoiceLimit === 0
            ? 0
            : invoiceLimit === -1
              ? safeUsage.invoiceDownloads > 0
                  ? 10
                  : 0
              : Math.min(
                    100,
                    (safeUsage.invoiceDownloads / invoiceLimit) * 100,
                );

    const imagePercent =
        isExpired || imageLimit === 0
            ? 0
            : imageLimit === -1
              ? safeUsage.imageUploads > 0
                  ? 10
                  : 0
              : Math.min(100, (safeUsage.imageUploads / imageLimit) * 100);

    const activeData =
        activeTab === "success"
            ? successPayments
            : activeTab === "pending"
              ? pendingPayments
              : failedPayments;

    const totalPages = Math.ceil(activeData.length / ITEMS_PER_PAGE);

    const paginatedData = activeData.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE,
    );

    const planName = subscription.plan.toUpperCase();

    const statusColor =
        normalizedStatus === "active"
            ? "#4ade80"
            : normalizedStatus === "expired"
              ? "#fb923c"
              : "#f87171";
    const statusLabel =
        normalizedStatus === "expired"
            ? "EXPIRED"
            : normalizedStatus.toUpperCase();

    const PLAN_COLORS = {
        free: "#94a3b8",
        starter: "#60a5fa",
        pro: "#a78bfa",
        enterprise: "#fb923c",
    };
    const planColor = PLAN_COLORS[planKey] || "#60a5fa";

    const paymentStatusStyle = (s) => {
        const st = (s || "success").toLowerCase();
        if (st === "success")
            return {
                bg: "rgba(74,222,128,0.1)",
                border: "rgba(74,222,128,0.2)",
                color: "#4ade80",
            };
        if (st === "failed")
            return {
                bg: "rgba(248,113,113,0.1)",
                border: "rgba(248,113,113,0.2)",
                color: "#f87171",
            };
        if (st === "pending")
            return {
                bg: "rgba(251,146,60,0.1)",
                border: "rgba(251,146,60,0.2)",
                color: "#fb923c",
            };
        return {
            bg: "rgba(148,163,184,0.1)",
            border: "rgba(148,163,184,0.2)",
            color: "#94a3b8",
        };
    };

    const renderTable = (list) => {
        if (!list.length) return <div className="sp-empty">No records</div>;

        return (
            <table className="sp-table">
                <thead>
                    <tr>
                        <th>Date</th>
                        <th>Plan</th>
                        <th>Billing</th>
                        <th>Amount</th>
                        <th>Status</th>
                    </tr>
                </thead>
                <tbody>
                    {list.map((p) => {
                        const pStyle = paymentStatusStyle(p.status);
                        const pc =
                            PLAN_COLORS[p.plan?.toLowerCase()] || "#60a5fa";

                        return (
                            <tr key={p._id}>
                                <td>
                                    {new Date(p.paidAt).toLocaleDateString(
                                        "en-IN",
                                    )}
                                </td>
                                <td>
                                    <span
                                        className="sp-pill"
                                        style={{
                                            background: `${pc}12`,
                                            borderColor: `${pc}30`,
                                            color: pc,
                                        }}
                                    >
                                        {p.plan}
                                    </span>
                                </td>
                                <td>
                                    <span
                                        className="sp-pill"
                                        style={{
                                            border: "none",
                                            borderRadius: "0",
                                        }}
                                    >
                                        {(p.billingCycle || "").toUpperCase()}
                                    </span>
                                </td>
                                <td>
                                    <span className="sp-amount">
                                        {formatCurrency(p.amountPaid)}
                                    </span>
                                </td>
                                <td>
                                    <span
                                        className="sp-pill"
                                        style={{
                                            background: pStyle.bg,
                                            borderColor: pStyle.border,
                                            color: pStyle.color,
                                        }}
                                    >
                                        {(p.status || "").toLowerCase()}
                                    </span>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        );
    };

    const renderMobileCards = (list) => {
        return (
            <div className="sp-mob-table">
                {list.map((item) => (
                    <div key={item._id} className="sp-pay-card">
                        <div className="sp-pay-top">
                            <span className="sp-pill">
                                {(item.status || "").toUpperCase()}
                            </span>
                            <span className="sp-pay-date">
                                {new Date(item.paidAt).toLocaleDateString(
                                    "en-IN",
                                )}
                            </span>
                        </div>
                        <div className="sp-pay-bottom">
                            <span>{item.plan}</span>
                            <span className="sp-pay-amount">
                                {formatCurrency(item.amountPaid)}
                            </span>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <>
            <div className="sp-root">
                <div className="sp-header">
                    <h1 className="sp-title">
                        Billing &amp; <em>Subscription</em>
                    </h1>
                </div>

                <div className="sp-card" style={{ "--accent": planColor }}>
                    <div
                        style={{
                            position: "absolute",
                            top: 0,
                            left: "10%",
                            right: "10%",
                            height: 1,
                            background: `linear-gradient(90deg, transparent, ${planColor}55, transparent)`,
                        }}
                    />
                    <div className="sp-card-header">
                        <div
                            className="sp-card-icon"
                            style={{
                                background: `${planColor}18`,
                                color: planColor,
                            }}
                        >
                            <CreditCard size={15} />
                        </div>
                        <div className="sp-card-title">Current Plan</div>
                    </div>
                    <div className="sp-card-body">
                        <div className="sp-plan-row">
                            <div>
                                <div
                                    className="sp-plan-name"
                                    style={{ color: planColor }}
                                >
                                    {planName}
                                </div>
                                <span
                                    className="sp-status-badge"
                                    style={{
                                        background: `${statusColor}12`,
                                        borderColor: `${statusColor}30`,
                                        color: statusColor,
                                    }}
                                >
                                    {normalizedStatus === "active" ? (
                                        <CheckCircle size={10} />
                                    ) : (
                                        <AlertTriangle size={10} />
                                    )}
                                    {statusLabel}
                                </span>
                            </div>
                            <div className="sp-days-box">
                                <div className="sp-days-label">
                                    Days Remaining
                                </div>
                                <div className="sp-days-value">
                                    {isExpired
                                        ? "0"
                                        : subscription.plan === "FREE"
                                          ? "Expired"
                                          : subscription.daysRemaining}
                                </div>
                            </div>
                        </div>

                        <div className="sp-meta-grid">
                            <div className="sp-meta-item">
                                <div className="sp-meta-label">
                                    <Calendar
                                        size={10}
                                        style={{
                                            display: "inline",
                                            marginRight: 5,
                                        }}
                                    />
                                    Start Date
                                </div>
                                <div className="sp-meta-value">{startDate}</div>
                            </div>
                            <div className="sp-meta-item">
                                <div className="sp-meta-label">
                                    <Clock
                                        size={10}
                                        style={{
                                            display: "inline",
                                            marginRight: 5,
                                        }}
                                    />
                                    Expiry Date
                                </div>
                                <div className="sp-meta-value">
                                    {expiryDate}
                                </div>
                            </div>
                            <div className="sp-meta-item">
                                <div className="sp-meta-label">
                                    <RefreshCw
                                        size={10}
                                        style={{
                                            display: "inline",
                                            marginRight: 5,
                                        }}
                                    />
                                    Billing Cycle
                                </div>
                                <div className="sp-meta-value">
                                    {(billingCycle || "").toUpperCase()}
                                </div>
                            </div>
                            <div className="sp-meta-item">
                                <div className="sp-meta-label">Currency</div>
                                <div className="sp-meta-value">
                                    {effectiveCurrency.currency} (
                                    {effectiveCurrency.symbol})
                                </div>
                            </div>
                        </div>

                        {isExpired && (
                            <div className="sp-expired-alert">
                                <AlertTriangle
                                    size={16}
                                    style={{ flexShrink: 0 }}
                                />
                                <span>
                                    Your{" "}
                                    <strong>
                                        {subscription.plan.toUpperCase()}
                                    </strong>{" "}
                                    plan has expired.{" "}
                                    <a
                                        href="#pricing"
                                        className="sp-upgrade-link"
                                    >
                                        Upgrade now
                                    </a>{" "}
                                    to continue using all features.
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Usage Card */}
                <div className="sp-card">
                    <div
                        style={{
                            position: "absolute",
                            top: 0,
                            left: "10%",
                            right: "10%",
                            height: 1,
                            background:
                                "linear-gradient(90deg, transparent, rgba(77,124,246,0.35), transparent)",
                        }}
                    />
                    <div className="sp-card-header">
                        <div
                            className="sp-card-icon"
                            style={{
                                background: "rgba(77,124,246,0.1)",
                                color: "#60a5fa",
                            }}
                        >
                            <FileSpreadsheet size={15} />
                        </div>
                        <div className="sp-card-title">Plan Usage</div>
                    </div>
                    <div className="sp-card-body">
                        <div className="sp-usage-item">
                            <div className="sp-usage-row">
                                <span className="sp-usage-label">
                                    <FileSpreadsheet size={12} /> Excel Exports
                                </span>
                                <span className="sp-usage-val">
                                    {safeUsage.excelExports} /{" "}
                                    {excelLimit === -1 ? "∞" : excelLimit}
                                </span>
                            </div>
                            <div className="sp-prog-track">
                                <div
                                    className="sp-prog-fill"
                                    style={{
                                        width: `${excelPercent}%`,
                                        background:
                                            "linear-gradient(90deg, #4d7cf6, #60a5fa)",
                                    }}
                                />
                            </div>
                        </div>
                        <div className="sp-usage-item">
                            <div className="sp-usage-row">
                                <span className="sp-usage-label">
                                    <FileText size={12} /> Invoice Downloads
                                </span>
                                <span className="sp-usage-val">
                                    {safeUsage.invoiceDownloads} /{" "}
                                    {invoiceLimit === -1 ? "∞" : invoiceLimit}
                                </span>
                            </div>
                            <div className="sp-prog-track">
                                <div
                                    className="sp-prog-fill"
                                    style={{
                                        width: `${invoicePercent}%`,
                                        background:
                                            "linear-gradient(90deg, #4ade80, #86efac)",
                                    }}
                                />
                            </div>
                        </div>
                        <div className="sp-usage-item">
                            <div className="sp-usage-row">
                                <span className="sp-usage-label">
                                    <Image size={12} /> Image Uploads
                                </span>
                                <span className="sp-usage-val">
                                    {safeUsage.imageUploads || 0} /{" "}
                                    {imageLimit === -1 ? "∞" : imageLimit}
                                </span>
                            </div>
                            <div className="sp-prog-track">
                                <div
                                    className="sp-prog-fill"
                                    style={{
                                        width: `${imagePercent}%`,
                                        background:
                                            "linear-gradient(90deg, #fb923c, #f97316)",
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Payment History */}
                <div className="sp-card">
                    <div className="sp-card-header">
                        <div className="sp-card-icon">
                            <Clock size={15} />
                        </div>
                        <div className="sp-card-title">Payment History</div>
                        <div className="sp-card-subtitle">
                            (Last 5 payment records)
                        </div>
                    </div>
                    <div className="sp-active-tab">
                        {["success", "pending", "failed"].map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                style={{
                                    padding: "6px 12px",
                                    borderRadius: 8,
                                    border: "1px solid #2e3d5c",
                                    background:
                                        activeTab === tab
                                            ? tab === "success"
                                                ? "rgba(74,222,128,0.15)"
                                                : tab === "pending"
                                                  ? "rgba(251,146,60,0.15)"
                                                  : "rgba(248,113,113,0.15)"
                                            : "transparent",
                                    color:
                                        tab === "success"
                                            ? "#4ade80"
                                            : tab === "pending"
                                              ? "#fb923c"
                                              : "#f87171",
                                    cursor: "pointer",
                                    fontSize: "0.75rem",
                                }}
                            >
                                {tab.toUpperCase()}
                            </button>
                        ))}
                    </div>

                    <div className="sp-card-body" style={{ padding: "16px" }}>
                        <h3
                            style={{
                                marginBottom: 10,
                                color:
                                    activeTab === "success"
                                        ? "#4ade80"
                                        : activeTab === "pending"
                                          ? "#fb923c"
                                          : "#f87171",
                            }}
                        >
                            {activeTab.charAt(0).toUpperCase() +
                                activeTab.slice(1)}{" "}
                            Payments
                        </h3>
                        {renderTable(paginatedData)}
                        {renderMobileCards(paginatedData)}
                    </div>
                </div>

                {totalPages > 1 && (
                    <div className="sp-pagination">
                        <button
                            className="sp-page-btn"
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage((p) => p - 1)}
                        >
                            <ChevronLeft size={14} />
                        </button>
                        {[...Array(totalPages)].map((_, i) => {
                            const pg = i + 1;
                            if (
                                pg === 1 ||
                                pg === totalPages ||
                                Math.abs(currentPage - pg) <= 1
                            ) {
                                return (
                                    <button
                                        key={i}
                                        className={`sp-page-btn ${currentPage === pg ? "active" : ""}`}
                                        onClick={() => setCurrentPage(pg)}
                                    >
                                        {pg}
                                    </button>
                                );
                            }
                            if (
                                pg === currentPage - 2 ||
                                pg === currentPage + 2
                            ) {
                                return (
                                    <span key={i} className="sp-page-dots">
                                        ···
                                    </span>
                                );
                            }
                            return null;
                        })}
                        <button
                            className="sp-page-btn"
                            disabled={currentPage === totalPages}
                            onClick={() => setCurrentPage((p) => p + 1)}
                        >
                            <ChevronRight size={14} />
                        </button>
                    </div>
                )}

                <div id="pricing">
                    <Pricing />
                </div>
            </div>
        </>
    );
}
